import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import ChatMessage from './components/ChatMessage';
import ChatVoiceNote from '@/pages/client/dashboard/components/ChatVoiceNote';
import VoiceInputButton from '@/components/feature/VoiceInputButton';
import { useMerchantVoice } from '@/hooks/useMerchantVoice';
import { invokeAssistant, loadAssistantHistory } from '@/lib/assistantChat';
import { uploadVoiceNote } from '@/lib/voiceStorage';
import type { VoiceResult } from '@/hooks/useVoiceDictation';
import type { Message } from './types';

const WELCOME =
  'Bonjour ! Je suis votre assistant personnel. Je suis connecté à votre boutique (produits, commandes, clients, transactions) et à vos applications installées (projets, tâches, tickets, formulaires, documents, fidélité). Posez-moi vos questions et je vous aiderai à piloter votre activité.';

/** Détecte une demande de CRÉATION de facture (et non une simple question). */
function isInvoiceRequest(text: string): boolean {
  const t = text.toLowerCase();
  if (!/(facture|facturer|facturation)/.test(t)) return false;
  return /(cr[eé]e|cr[eé]er|g[eé]n[eè]re|g[eé]n[eé]rer|fais|faire|[eé]tablis|[eé]tablir|envoie|envoyer|pr[eé]pare|pr[eé]parer|[eé]mets|[eé]mettre|facter)/.test(t);
}

/** Détecte une demande de CRÉATION de devis. */
function isQuoteRequest(text: string): boolean {
  const t = text.toLowerCase();
  if (!/devis/.test(t)) return false;
  return /(cr[eé]e|cr[eé]er|g[eé]n[eè]re|g[eé]n[eé]rer|fais|faire|[eé]tablis|[eé]tablir|envoie|envoyer|pr[eé]pare|pr[eé]parer|[eé]mets|[eé]mettre|propose)/.test(t);
}

/** Détecte une demande de comparatif (semaine vs semaine, périodes, etc.). */
function isComparisonRequest(text: string): boolean {
  return /(compar|comparatif|vs\b|semaine pass|semaine derni|par rapport [aà] (la )?semaine|ce mois.*mois dernier|mois dernier.*ce mois)/i.test(text);
}

const SUGGESTIONS = [
  { icon: 'ri-line-chart-line', text: 'Comment se portent mes ventes ?' },
  { icon: 'ri-bar-chart-grouped-line', text: 'Compare mes ventes avec la semaine passée' },
  { icon: 'ri-archive-line', text: 'Quels produits dois-je réapprovisionner ?' },
  { icon: 'ri-receipt-line', text: 'Génère une facture pour mon client' },
  { icon: 'ri-file-list-3-line', text: 'Crée un devis pour mon client' },
];

export default function MonAssistantPage() {
  const { user } = useAuth();
  const merchantVoice = useMerchantVoice();
  const [messages, setMessages] = useState<Message[]>([{ role: 'assistant', content: WELCOME }]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Charger l'historique dans le fil de conversation (ordre chronologique)
  useEffect(() => {
    if (!user || historyLoaded) return;
    let active = true;
    void (async () => {
      const history = await loadAssistantHistory(user.id, 200);
      if (!active) return;
      if (history.length > 0) {
        setMessages([{ role: 'assistant', content: WELCOME }, ...history.map((m) => ({ role: m.role, content: m.content, audio: m.audio }))]);
      }
      setHistoryLoaded(true);
    })();
    return () => { active = false; };
  }, [user, historyLoaded]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, loading]);

  const resetConversation = useCallback(() => {
    setMessages([{ role: 'assistant', content: WELCOME }]);
    setError(null);
    setInput('');
    inputRef.current?.focus();
  }, []);

  const runAssistant = useCallback(
    async (body: Record<string, unknown>, userContent: string, audio?: { url: string; duration: number }) => {
      setMessages((prev) => [...prev, { role: 'user', content: userContent, audio }]);
      setInput('');
      setError(null);
      setLoading(true);
      try {
        const payload = await invokeAssistant(body);
        setMessages((prev) => [...prev, { role: 'assistant', content: payload.reply, invoice: payload.invoice }]);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Une erreur est survenue';
        setError(msg);
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: 'Désolé, je n\u2019ai pas pu répondre. Réessayez dans un instant.' },
        ]);
      } finally {
        setLoading(false);
        inputRef.current?.focus();
      }
    },
    [],
  );

  const handleSend = useCallback(
    async (
      raw?: string,
      voice?: { url: string; duration: number; blob?: Blob; mime?: string },
    ) => {
      const text = (raw ?? input).trim();
      if (!text || loading || !user) return;

      // 🎙️ On téléverse l'audio pour obtenir une URL persistante : la note
      // reste réécoutable même après un rechargement de la page.
      let audio: { url: string; duration: number } | undefined = voice
        ? { url: voice.url, duration: voice.duration }
        : undefined;
      if (voice?.blob) {
        try {
          const persisted = await uploadVoiceNote(voice.blob, user.id, voice.mime);
          if (persisted) audio = { url: persisted, duration: voice.duration };
        } catch { /* on garde l'URL locale */ }
      }
      const extra = audio ? { audio } : {};

      // 🧾 Devis : action serveur réelle (création + notification du client).
      if (isQuoteRequest(text)) {
        void runAssistant({ action: 'create_document', docType: 'quote', message: text, userId: user.id, ...extra }, text, audio);
        return;
      }
      // 🧾 Facture : action serveur réelle (création + notification du client).
      if (isInvoiceRequest(text)) {
        void runAssistant({ action: 'create_invoice', message: text, userId: user.id, ...extra }, text, audio);
        return;
      }
      // 📊 Comparatif : chiffres réels calculés côté serveur.
      if (isComparisonRequest(text)) {
        void runAssistant({ action: 'sales_comparison', userId: user.id, ...extra }, text, audio);
        return;
      }
      void runAssistant({ message: text, userId: user.id, ...extra }, text, audio);
    },
    [input, loading, user, runAssistant],
  );

  // ── Note vocale : le texte part DIRECTEMENT à l'IA, jamais dans le champ ──
  const handleVoiceNote = useCallback((result: VoiceResult) => {
    void handleSend(result.text, {
      url: result.audioUrl,
      duration: result.durationSeconds,
      blob: result.audioBlob,
      mime: result.mimeType,
    });
  }, [handleSend]);

  const handleWeeklySummary = useCallback(() => {
    if (loading || !user) return;
    runAssistant(
      { weeklySummary: true, userId: user.id },
      'Résumé de la semaine : tops + alertes',
    );
  }, [loading, user, runAssistant]);

  const showSuggestions = messages.length <= 1 && !loading;

  return (
    <div className="relative w-full min-w-0 max-w-full flex flex-col h-[calc(100dvh-3.5rem)] md:h-[calc(100dvh-4rem)] overflow-hidden bg-background-50">
      {/* En-tête compact */}
      <header className="flex items-center gap-2 sm:gap-2.5 px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 border-b border-background-200/70 flex-shrink-0">
        <div className="relative flex-shrink-0">
          <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center">
            <i className="ri-robot-2-line text-base"></i>
          </div>
          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-accent-500 border-2 border-background-50"></span>
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm sm:text-base font-bold font-heading text-foreground-950 leading-tight truncate">
            Mon Assistant
          </h2>
          {loading ? (
            <p className="text-[11px] text-primary-600 truncate flex items-center gap-1">
              <span>en train d&apos;écrire</span>
              <span className="inline-flex gap-0.5">
                <span className="w-1 h-1 rounded-full bg-primary-500 animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-1 h-1 rounded-full bg-primary-500 animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-1 h-1 rounded-full bg-primary-500 animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </span>
            </p>
          ) : (
            <p className="text-[11px] text-foreground-500 truncate">Connecté à votre boutique et vos apps</p>
          )}
        </div>
        <button
          onClick={handleWeeklySummary}
          disabled={loading}
          title="Résumé hebdomadaire (tops + alertes)"
          className="flex items-center justify-center gap-1.5 w-9 sm:w-auto h-9 sm:px-3 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer flex-shrink-0 whitespace-nowrap"
        >
          <i className="ri-file-list-3-line text-base"></i>
          <span className="hidden sm:inline text-xs font-medium">Résumé hebdo</span>
        </button>
        <button
          onClick={resetConversation}
          title="Nouvelle conversation"
          className="flex items-center justify-center gap-1.5 w-9 sm:w-auto h-9 sm:px-3 rounded-full bg-background-100 border border-background-200/70 text-foreground-600 hover:text-foreground-900 hover:bg-background-200/60 transition-colors cursor-pointer flex-shrink-0 whitespace-nowrap"
        >
          <i className="ri-add-line text-base"></i>
          <span className="hidden sm:inline text-xs font-medium">Nouvelle</span>
        </button>
      </header>

      {/* Messages — prend tout l'espace disponible */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        <div className="w-full max-w-3xl mx-auto px-3 sm:px-4 md:px-6 py-3 sm:py-5 space-y-4 sm:space-y-5">
          {messages.map((msg, i) => (
            msg.audio ? (
              <div key={i} className="flex justify-end">
                <div className="max-w-[85%] sm:max-w-[75%] px-3.5 py-2.5 rounded-2xl rounded-tr-md bg-accent-500 text-background-50">
                  <ChatVoiceNote src={msg.audio.url} durationSeconds={msg.audio.duration} transcript={msg.content} />
                </div>
              </div>
            ) : (
              <ChatMessage key={i} role={msg.role} content={msg.content} voice={merchantVoice} invoice={msg.invoice} />
            )
          ))}

          {loading && (
            <div className="flex items-start gap-2.5">
              <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-robot-2-line text-sm"></i>
              </div>
              <div className="flex gap-1 px-4 py-3.5 bg-background-100 rounded-2xl rounded-tl-md">
                <span
                  className="w-2 h-2 bg-foreground-300 rounded-full animate-bounce"
                  style={{ animationDelay: '0ms' }}
                ></span>
                <span
                  className="w-2 h-2 bg-foreground-300 rounded-full animate-bounce"
                  style={{ animationDelay: '150ms' }}
                ></span>
                <span
                  className="w-2 h-2 bg-foreground-300 rounded-full animate-bounce"
                  style={{ animationDelay: '300ms' }}
                ></span>
              </div>
            </div>
          )}

          {/* Suggestions — desktop uniquement */}
          {showSuggestions && (
            <div className="hidden sm:flex flex-wrap gap-2 pt-1">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s.text}
                  onClick={() => handleSend(s.text)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-background-50 border border-background-200/70 rounded-full text-xs text-foreground-600 cursor-pointer hover:bg-background-100 hover:border-primary-300 transition-colors whitespace-nowrap"
                >
                  <i className={`${s.icon} text-xs text-primary-500`}></i>
                  {s.text}
                </button>
              ))}
            </div>
          )}

          <div ref={chatEndRef} />
        </div>
      </div>

      {/* Erreur */}
      {error && (
        <div className="px-3 sm:px-4 md:px-6 pb-2 flex-shrink-0">
          <div className="w-full max-w-3xl mx-auto flex items-start gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
            <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
            <span className="flex-1 min-w-0 break-words">{error}</span>
            <button
              onClick={() => setError(null)}
              title="Fermer"
              className="text-red-500 cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>
      )}

      {/* Zone basse : saisie épurée */}
      <div className="flex-shrink-0 border-t border-background-200/70 bg-background-50">
        <div className="w-full max-w-3xl mx-auto px-3 sm:px-4 md:px-6 py-2.5 sm:py-3.5">
          <div className="flex items-end gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Posez votre question…"
              className="flex-1 min-w-0 px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-full text-sm focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 text-foreground-900 placeholder:text-foreground-400"
            />
            <VoiceInputButton userId={user?.id} onVoiceResult={handleVoiceNote} />
            <button
              onClick={() => handleSend()}
              disabled={!input.trim() || loading}
              title="Envoyer"
              className="w-11 h-11 rounded-full bg-primary-500 text-background-50 flex items-center justify-center cursor-pointer hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            >
              <i className="ri-send-plane-fill"></i>
            </button>
          </div>
          <p className="hidden sm:block text-[11px] text-foreground-400 mt-1.5 text-center">
            L&apos;assistant analyse les données réelles de votre boutique.
          </p>
        </div>
      </div>
    </div>
  );
}